import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  crmAnalyticsApi,
  type DateRangeParams,
  type ShowingDrilldownItem
} from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { PropertyGroupHeader } from "./PropertyGroupHeader";
import { AssigneeFilterChip } from "./AssigneeFilterChip";
import type { UnitFilterParams } from "./UnitsFilter";
import {
  AnalyticsCard,
  DEFAULT_PER_PAGE,
  DRILLDOWN_ROWS_BASE_CLASSNAME,
  FOUR_COLS_DRILLDOWN_GRID_CLASSNAME,
  SHOWINGS_DRILLDOWN_COLUMNS,
  type ShowingsAnalyticsCard
} from "../constants";
import { groupByProperty, propertyKeyedSelector } from "../utils";
import { cx } from "shared/utils";

type ShowingsCardConfig = {
  title: string;
  queryKeyRoot: string;
  fetcher: typeof crmAnalyticsApi.getShowingsScheduled;
};

const SHOWING_ROWS_TEXT_CLASSNAME =
  "text-sm font-medium text-content-700 truncate";

const SHOWINGS_CARD_CONFIG: Record<ShowingsAnalyticsCard, ShowingsCardConfig> =
  {
    [AnalyticsCard.SHOWINGS_SCHEDULED]: {
      title: "Showings scheduled",
      queryKeyRoot: "CRM_ANALYTICS_SHOWINGS_SCHEDULED",
      fetcher: crmAnalyticsApi.getShowingsScheduled
    },
    [AnalyticsCard.SHOWINGS_COMPLETED]: {
      title: "Showings completed",
      queryKeyRoot: "CRM_ANALYTICS_SHOWINGS_COMPLETED",
      fetcher: crmAnalyticsApi.getShowingsCompleted
    },
    [AnalyticsCard.SHOWINGS_NO_SHOWS]: {
      title: "Showing no shows",
      queryKeyRoot: "CRM_ANALYTICS_SHOWINGS_NO_SHOWS",
      fetcher: crmAnalyticsApi.getShowingsNoShows
    }
  };

const PLACEHOLDER = "--";

const ShowingRowContent = ({ item }: { item: ShowingDrilldownItem }) => (
  <>
    <p className={SHOWING_ROWS_TEXT_CLASSNAME}>
      {item.lead_name ?? PLACEHOLDER}
    </p>
    <p className={SHOWING_ROWS_TEXT_CLASSNAME}>
      {item.unit_title ?? PLACEHOLDER}
    </p>
    <p className={SHOWING_ROWS_TEXT_CLASSNAME}>
      {item.partner_user_name ?? PLACEHOLDER}
    </p>
    <div className="text-right">
      <p className={cx(SHOWING_ROWS_TEXT_CLASSNAME, "whitespace-nowrap")}>
        {moment(item.transitioned_at).format("MMMM D, YYYY")}
      </p>
      <p className={SHOWING_ROWS_TEXT_CLASSNAME}>
        {moment(item.transitioned_at).format("h:mm A")}
      </p>
    </div>
  </>
);

const ShowingRow = ({ item }: { item: ShowingDrilldownItem }) => {
  if (item.lead_id == null)
    return (
      <div
        className={cx(
          FOUR_COLS_DRILLDOWN_GRID_CLASSNAME,
          DRILLDOWN_ROWS_BASE_CLASSNAME
        )}
      >
        <ShowingRowContent item={item} />
      </div>
    );
  return (
    <Link
      to={`/leads/${item.lead_id}`}
      className={cx(
        FOUR_COLS_DRILLDOWN_GRID_CLASSNAME,
        DRILLDOWN_ROWS_BASE_CLASSNAME
      )}
    >
      <ShowingRowContent item={item} />
    </Link>
  );
};

export const ShowingsDrilldownModal = ({
  card,
  opened,
  onClose,
  dateParams,
  filterParams
}: {
  card: ShowingsAnalyticsCard;
  opened: boolean;
  onClose: () => void;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
}) => {
  const [page, setPage] = useState(1);
  const [assigneeIds, setAssigneeIds] = useState<number[] | null>(null);
  const config = SHOWINGS_CARD_CONFIG[card];

  useEffect(() => {
    if (opened) {
      setPage(1);
      setAssigneeIds(null);
    }
  }, [opened]);

  useEffect(() => {
    setPage(1);
  }, [assigneeIds]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      config.queryKeyRoot,
      dateParams,
      page,
      filterParams,
      assigneeIds
    ],
    queryFn: () =>
      config.fetcher({
        ...filterParams,
        ...dateParams,
        page,
        per_page: Number(DEFAULT_PER_PAGE),
        ...(assigneeIds != null ? { assignee_ids: assigneeIds } : {})
      }),
    enabled: opened,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const totalPages = data?.page_count ?? 0;
  const pagination = useMemo(
    () => ({ total: totalPages, current: page, onChange: setPage }),
    [totalPages, page]
  );
  const groups = useMemo(
    () => groupByProperty(items, propertyKeyedSelector),
    [items]
  );

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title={config.title}
      gridClassName={FOUR_COLS_DRILLDOWN_GRID_CLASSNAME}
      columns={SHOWINGS_DRILLDOWN_COLUMNS}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={items.length === 0}
      emptyText="No showings found"
      pagination={pagination}
      headerExtra={
        <div className="flex justify-end">
          <AssigneeFilterChip
            selectedIds={assigneeIds}
            onChange={setAssigneeIds}
          />
        </div>
      }
    >
      {groups.map((group) => (
        <Fragment key={group.key}>
          <PropertyGroupHeader name={group.name} />
          {group.items.map((item) => (
            <ShowingRow key={item.id} item={item} />
          ))}
        </Fragment>
      ))}
    </AnalyticsDetailModal>
  );
};
